<template>
	<SectionCard title="Orari di lavoro" icon="mdi-clock-outline" class="mb-4" data-tour="hours">
		<div class="wh-hint mb-3">
			Gli orari in cui si lavorano gli ordini. La Board ordini misura i tempi solo dentro queste fasce: un ordine arrivato alle 17 e lavorato alle 9 del giorno dopo ha
			impiegato un'ora e poco più, non sedici. Le festività nazionali (Pasqua e Pasquetta comprese) si escludono da sole. Nessun giorno aperto = i tempi si contano 24 ore su 24.
		</div>

		<div class="wh-grid">
			<div class="wh-row wh-head">
				<div>Giorno</div>
				<div>Mattina</div>
				<div>Pomeriggio</div>
			</div>
			<div v-for="row in value" :key="row.day" class="wh-row" :class="{ 'is-closed': !row.open }">
				<div class="wh-day">
					<v-switch v-model="row.open" color="primary" dense hide-details class="mt-0 pt-0" :label="dayName(row.day)" @change="onToggle(row)" />
				</div>
				<div class="wh-slot">
					<v-text-field v-model="row.slots[0].from" type="time" dense outlined hide-details="auto" :disabled="!row.open" label="dalle" :error-messages="slotError(row, 0)" />
					<v-text-field v-model="row.slots[0].to" type="time" dense outlined hide-details="auto" :disabled="!row.open" label="alle" />
				</div>
				<div class="wh-slot">
					<v-text-field v-model="row.slots[1].from" type="time" dense outlined hide-details="auto" :disabled="!row.open" label="dalle" :error-messages="slotError(row, 1)" />
					<v-text-field v-model="row.slots[1].to" type="time" dense outlined hide-details="auto" :disabled="!row.open" label="alle" />
					<!-- lo spazio del pulsante resta anche quando non serve: le righe restano allineate -->
					<v-btn icon x-small title="Togli il pomeriggio" :style="{ visibility: row.open && (row.slots[1].from || row.slots[1].to) ? 'visible' : 'hidden' }" @click="clearSlot(row, 1)">
						<v-icon x-small>mdi-close</v-icon>
					</v-btn>
				</div>
			</div>
		</div>

		<div class="d-flex flex-wrap align-center mt-3" style="gap: 8px">
			<v-btn small outlined color="primary" :disabled="!value[0] || !value[0].open" @click="copyMonday">
				<v-icon left small>mdi-content-copy</v-icon>Copia il lunedì sugli altri giorni feriali
			</v-btn>
			<span class="wh-hint">Il pomeriggio è facoltativo: lascialo vuoto per l'orario continuato. Per salvare usa Salva in alto.</span>
		</div>
	</SectionCard>
</template>

<script>
// Orari di lavoro del negozio (profile.working_hours): una riga per giorno, mattina e pomeriggio.
// v-model = [{ day: 1..7, open, slots: [{ from, to }, { from, to }] }]; il backend scarta le fasce vuote.
import SectionCard from '@/components/core/SectionCard.vue';

const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

/** Settimana per il form: sempre 7 giorni con due fasce, anche vuote */
export function weekForForm(saved) {
	const rows = Array.isArray(saved) ? saved : [];
	return [1, 2, 3, 4, 5, 6, 7].map(day => {
		const row = rows.find(r => Number(r && r.day) === day) || {};
		const slots = Array.isArray(row.slots) ? row.slots : [];
		return {
			day,
			open: Boolean(row.open),
			slots: [0, 1].map(i => ({ from: (slots[i] && slots[i].from) || '', to: (slots[i] && slots[i].to) || '' })),
		};
	});
}

// errore della fascia i di un giorno, oppure '' se va bene
const slotProblem = (row, i) => {
	const s = row.slots[i];
	if (!row.open || (!s.from && !s.to)) return '';
	if (!s.from || !s.to) return 'Orario incompleto';
	if (s.from >= s.to) return "L'inizio deve essere prima della fine";
	if (i === 1 && row.slots[0].to && s.from < row.slots[0].to) return 'Il pomeriggio inizia prima della fine della mattina';
	return '';
};

/** Giorni con orari da correggere, per bloccare il salvataggio */
export function invalidDays(week) {
	return (week || []).filter(row => slotProblem(row, 0) || slotProblem(row, 1)).map(row => DAYS[row.day - 1]);
}

export default {
	name: 'WorkingHoursCard',
	components: { SectionCard },
	props: {
		value: { type: Array, required: true },
	},
	methods: {
		dayName: day => DAYS[day - 1],
		// aprendo un giorno vuoto si propone l'orario piu' comune, da correggere
		onToggle(row) {
			if (row.open && !row.slots[0].from && !row.slots[0].to) {
				row.slots[0].from = '09:00';
				row.slots[0].to = '13:00';
				row.slots[1].from = '14:00';
				row.slots[1].to = '18:00';
			}
		},
		clearSlot(row, i) {
			row.slots[i].from = '';
			row.slots[i].to = '';
		},
		copyMonday() {
			const monday = this.value[0];
			for (const row of this.value.slice(1, 5)) {
				row.open = monday.open;
				row.slots.forEach((slot, i) => {
					slot.from = monday.slots[i].from;
					slot.to = monday.slots[i].to;
				});
			}
		},
		slotError(row, i) {
			const problem = slotProblem(row, i);
			return problem ? [problem] : [];
		},
	},
};
</script>

<style scoped>
.wh-hint {
	font-size: 13px;
	color: rgba(0, 0, 0, 0.6);
}
.wh-row {
	display: grid;
	grid-template-columns: 170px 1fr 1fr;
	gap: 16px;
	align-items: center;
	padding: 6px 0;
	border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.wh-head {
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: rgba(0, 0, 0, 0.5);
	padding-top: 0;
}
.wh-slot {
	display: flex;
	align-items: center;
	gap: 8px;
}
.wh-row.is-closed .wh-slot {
	opacity: 0.5;
}
@media (max-width: 700px) {
	.wh-row {
		grid-template-columns: 1fr;
		gap: 6px;
	}
	.wh-head {
		display: none;
	}
}
</style>
